import { validateField, validateFields } from "./lib/validate.js";
import { removeFieldError, handleErrors, parseError, showError } from "./lib/errors.js";
import { loadRecaptcha, getRecaptchaToken } from "./lib/recaptcha.js";
import { removeAllErrors } from "./lib/errors.js";
import { formatFieldData, handleSubmit } from "./lib/submit.js";

import type {
  _FieldError,
  _SFMCValue,
  _UnparsedError,
  Brand,
  BrandFullName,
  Events,
  Field,
  FieldErrorMessages,
  Form,
  Options,
  Settings,
  Value
} from "./lib/types.js";

export type {
  _FieldError,
  _SFMCValue,
  _UnparsedError,
  Events,
  Field,
  FieldErrorMessages,
  Form,
  Options,
  Settings,
  Value
};

/**
 * __createForm__
 *
 * Creates a form object with methods for
 * submitting and validating & initializes
 * form on specified element
 *
 * @param settings.el - HTMLElement or string selector
 * @param options - Options object for form configuration
 * @returns Form object
 *
 * @public
 */
export default function createForm(el: HTMLElement | string, options: Options): Form {
  try {
    if (typeof el === "string") {
      el = document.querySelector(el) as HTMLElement;
    }

    if (!el) {
      throw new Error(`Could not find element`);
    }

    const defaults: Settings = {
      brand: "WWW",
      dataExtension: "FED_Testing",
      el,
      emailPreferencesURL: null,
      errors: true,
      errorEl: el,
      errorClass: "form-error",
      errorFieldClass: "form-error-field",
      errorFieldListClass: "form-field-errors",
      errorListClass: "form-errors",
      errorPosition: "top",
      events: {
        error() {},
        errorsRemoved() {},
        recaptchaSubmit() {},
        recaptchaLoaded() {},
        recaptchaVerification() {},
        submit() {},
        success() {}
      },
      internal: {
        brandFullName: "Wolverine_World_Wide",
        values: [],
        data: null,
        errors: [],
        parsedErrors: [],
        ready: true
      },
      method: "DE",
      name: null,
      recaptcha: false,
      recaptchaLoaded: false,
      recaptchaSiteKey: "6LfEEdAZAAAAAFkQnHboH4NGsRUUJAwLD4YtXoM2",
      recaptchaTimeout: 3000,
      recaptchaWidget: null,
      submitEl:
        (el.querySelector("button[type=submit]") as HTMLButtonElement) ||
        (el.querySelector("input[type=submit]") as HTMLInputElement),
      submitRecaptchaToken: false,
      testing: false,
      validateOnBlur: false,
      disableButtonWhileRecaptchaLoading: true
    };

    if (options) {
      if (options.method === "SFMC") {
        options.method = "DE";
      }

      if (options.method === "WISDM") {
        options.method = "BM";
      }
    }

    const settings: Settings = Object.assign(defaults, options);

    if (!settings.el) {
      throw new Error("Could not find element");
    }

    if (!settings.name && !settings.el.dataset.name) {
      throw new Error("Form options must have 'name' property or form element must have 'data-name' attribute");
    }

    const formatName = (name: string): string => {
      const noSpaces = name.replace(/\s+/g, "-").trim();
      const splitName = noSpaces.split("-");
      const camelCase = splitName
        .map((word, index, arr) => {
          if (arr.length === 1) {
            return word;
          }

          if (index === 0) {
            return word.toLowerCase();
          }

          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join("");

      return camelCase;
    };

    settings.name = formatName(settings.name || settings.el.dataset.name);
    const fields = Array.from(settings.el.querySelectorAll("[data-field]")) as Field[];

    if (!fields.length) {
      throw new Error("No fields found");
    }

    if (!["DE", "MC", "BM"].includes(settings.method)) {
      settings.method = "DE";
      console.warn("Invalid method. Using default: DE");
    }

    if (settings.method === "DE") {
      if (!options || typeof options.dataExtension === "undefined") {
        console.warn("No data extension provided. Using default: FED_Testing");
      }

      if (!options || typeof options.brand === "undefined") {
        console.warn("No brand provided. Using default: WWW");
      }
    }

    if (settings.method === "BM") {
      try {
        function getNested(obj: any, ...args: string[]) {
          return args.reduce((acc, arg) => (acc && acc[arg] ? acc[arg] : undefined), obj);
        }

        settings.emailPreferencesURL =
          getNested(window, "app", "urls", "emailPreferencesUrl") || getNested(window, "emailPreferencesURL") || null;

        if (!settings.emailPreferencesURL) {
          throw new Error(
            "No window.emailPreferencesURL or window.app.urls.emailPreferenceUrl found. Required for WISDM method."
          );
        }
      } catch {
        throw new Error("Error getting email preferences URL.");
      }

      settings.recaptcha = true;
    }

    const brandFullNames: {
      [key in Brand]: BrandFullName;
    } = {
      WWW: "Wolverine_World_Wide",
      BTS: "Bates",
      CAT: "CAT",
      CHA: "Chaco",
      HDF: "Harley-Davidson",
      HUS: "Hush_Puppies",
      HYT: "Hytest",
      KED: "Keds",
      MER: "Merrell",
      PRO: "Pro-Keds",
      SAU: "Saucony",
      SPE: "Sperry",
      WOL: "Wolverine"
    };

    settings.internal.brandFullName = brandFullNames[settings.brand];

    fields.forEach((field) => {
      field.name = field.dataset.field;

      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
        field.addEventListener("keyup", (event) => {
          const { key } = event as KeyboardEvent;

          if (key === "Enter") {
            event.preventDefault();
            submit();
          }
        });
      }

      function handleFieldChange() {
        if (settings.validateOnBlur === false) return;

        if (settings.validateOnBlur === "always") {
          const error = validateField(field, settings);
          removeFieldError(field, settings);

          if (error) {
            const parsedError = parseError(error);
            showError(parsedError, settings);
          }
        } else if (settings.validateOnBlur === "error") {
          if (field.errorEl) {
            const error = validateField(field, settings);

            if (!error) {
              removeFieldError(field, settings);
            }
          }
        }
      }

      if (field.type === "checkbox" || field.type === "select-one" || field.type === "select-multiple") {
        field.addEventListener("change", () => {
          if (field.errorEl) {
            handleFieldChange();
          }
        });
      } else if (field instanceof HTMLDivElement || field instanceof HTMLFieldSetElement) {
        const radios = Array.from(field.querySelectorAll("input[type=radio]")) as Field[];

        radios.forEach((radio) => {
          radio.addEventListener("change", () => {
            if (field.errorEl) {
              handleFieldChange();
            }
          });
        });
      } else {
        field.addEventListener("blur", handleFieldChange);
      }
    });

    async function submit() {
      try {
        if (!settings.internal.ready) return;

        settings.internal.ready = false;
        settings.internal.errors = [];

        removeAllErrors(fields, settings);

        settings.internal.errors.push(...validateFields(fields, settings));
        settings.internal.values = fields.map((field) => formatFieldData(field)).concat(settings.values || []);
        settings.internal.data = Object.fromEntries([
          ["Country", "US"],
          ["CountryCode", "US"],
          ["LanguageCode", "en-US"],
          ["IsWelcome", false],
          ...settings.internal.values.map((item) => [item.name, item.value])
        ]);

        const updatedData = settings.events.submit(settings.internal.data);

        if (updatedData) {
          settings.internal.data = updatedData;
        }

        let requiredFields: string[] = [];

        if (settings.method === "BM") {
          requiredFields = ["EmailAddress"];
        }

        if (settings.method === "MC") {
          requiredFields = ["EmailAddress"];

          if (!settings.eventDefinitionKey && !settings.internal.data.EventDefinitionKey) {
            settings.internal.errors.push({
              errorCode: 6,
              errorMessage: "Missing eventDefinitionKey"
            });
          }
        }

        const missingFields = requiredFields.filter((field) => settings.internal.data[field] === undefined);

        if (missingFields.length) {
          settings.internal.errors.push({
            errorCode: 11,
            errorMessage: `Missing required field${missingFields.length > 1 ? "s" : ""}: ${missingFields.join(", ")}`
          });
        }

        if (settings.internal.errors.length) {
          const errors = Array.from(new Map(settings.internal.errors.map((item) => [item["field"], item])).values());

          handleErrors(errors, settings);
          settings.internal.ready = true;

          return;
        }

        if (settings.recaptcha && settings.recaptchaLoaded) {
          getRecaptchaToken(settings);
        } else {
          await handleSubmit(settings);
          settings.internal.ready = true;
        }
      } catch (error) {
        console.log(error);
      }
    }

    async function recaptchaCallback(token: string) {
      settings.recaptchaToken = token;
      settings.events.recaptchaSubmit(token);
      await handleSubmit(settings);
      settings.internal.ready = true;
    }

    if (settings.recaptcha) {
      loadRecaptcha(recaptchaCallback, settings).then(() => {
        if (!settings.recaptchaLoaded) {
          return;
        }
      });
    }

    if (settings.submitEl) {
      settings.submitEl.addEventListener("click", submit);
    }

    return {
      el: settings.el,
      error(error: _UnparsedError | string, show: boolean): void {
        if (typeof error === "string") {
          error = {
            errorCode: null,
            errorMessage: error
          };
        }

        settings.internal.errors.push(error);

        if (show) {
          removeAllErrors(fields, settings);
          handleErrors([error], settings);
        }
      },
      name: settings.name,
      settings,
      submit,
      on(event, func): void {
        settings.events[event] = func;
      }
    };
  } catch (error) {
    console.error("createForm: " + (error instanceof Error ? error.message : error));
    return null;
  }
}
